/**
 * Rollenableitung für die Oberfläche — serverseitig bleibt die Wahrheit,
 * hier nur: welche Schaltfläche erscheint (nicht bloß disabled).
 */

import { mindestens, type Rolle } from "@cubic/shared";

export type SchreibAktion =
  | "projekt_anlegen"
  | "aufgabe_anlegen"
  | "aufgabe_aendern"
  | "aufgabe_status"
  | "aufgabe_zuweisen"
  | "aufgabe_archivieren"
  | "meilenstein_anlegen"
  | "projekt_archivieren";

/**
 * Darf die Person diese Schreib-Schaltfläche sehen?
 * `nurLesend` blendet alles Schreibende aus.
 */
export function darfAktion(optionen: {
  aktion: SchreibAktion;
  rolle?: Rolle | null;
  rechte?: readonly string[];
  nurLesend?: boolean;
}): boolean {
  if (optionen.nurLesend) return false;

  const rechte = optionen.rechte ?? [];
  const admin = rechte.includes("system.admin");

  switch (optionen.aktion) {
    case "projekt_anlegen":
      return admin || rechte.includes("projekte.verwalten");
    case "aufgabe_anlegen":
    case "aufgabe_aendern":
    case "aufgabe_status":
    case "aufgabe_zuweisen":
    case "meilenstein_anlegen":
      // Deckt sich mit `darfSchreiben()` in packages/shared/src/rechte.ts,
      // was `legeMeilensteinAn()` serverseitig tatsächlich prüft — Rolle
      // mindestens Mitglied.
      return (
        admin ||
        (optionen.rolle != null && mindestens(optionen.rolle, "mitglied"))
      );
    case "aufgabe_archivieren":
      return (
        admin ||
        (optionen.rolle != null && mindestens(optionen.rolle, "betreuer"))
      );
    case "projekt_archivieren":
      // Deckt sich mit `archiviereProjekt()`: `darfVerwalten` oder
      // `system.admin`. Ein ganzes Projekt wegzuräumen ist mehr als eine
      // Aufgabe zu archivieren, deshalb die höhere Rolle.
      return (
        admin ||
        (optionen.rolle != null && mindestens(optionen.rolle, "verwalter"))
      );
    default:
      return false;
  }
}

/** Vertrag: `darfAnlegen(rolle)` — Aufgabe anlegen. */
export function darfAnlegen(
  rolle: Rolle | null | undefined,
  optionen: { nurLesend?: boolean; rechte?: readonly string[] } = {},
): boolean {
  return darfAktion({
    aktion: "aufgabe_anlegen",
    rolle,
    nurLesend: optionen.nurLesend,
    rechte: optionen.rechte,
  });
}

/** Vertrag: `darfProjektArchivieren(rolle)`. */
export function darfProjektArchivieren(
  rolle: Rolle | null | undefined,
  optionen: { nurLesend?: boolean; rechte?: readonly string[] } = {},
): boolean {
  return darfAktion({
    aktion: "projekt_archivieren",
    rolle,
    nurLesend: optionen.nurLesend,
    rechte: optionen.rechte,
  });
}

/** Vertrag: `darfAendern(rolle)`. */
export function darfAendern(
  rolle: Rolle | null | undefined,
  optionen: { nurLesend?: boolean; rechte?: readonly string[] } = {},
): boolean {
  return darfAktion({
    aktion: "aufgabe_aendern",
    rolle,
    nurLesend: optionen.nurLesend,
    rechte: optionen.rechte,
  });
}

export function darfQualitaetsGateAendern(
  rolle: Rolle | null | undefined,
  optionen: { nurLesend?: boolean; rechte?: readonly string[] } = {},
): boolean {
  if (optionen.nurLesend) return false;
  const rechte = optionen.rechte ?? [];
  return (
    rolle != null &&
    mindestens(rolle, "verwalter") &&
    (rechte.includes("gitlab.konfigurieren") ||
      rechte.includes("system.admin"))
  );
}

export function darfQualitaetsGatePruefen(
  rolle: Rolle | null | undefined,
  optionen: { nurLesend?: boolean } = {},
): boolean {
  return (
    !optionen.nurLesend &&
    rolle != null &&
    mindestens(rolle, "mitglied")
  );
}

export function darfQualitaetsGateUebersteuern(
  rolle: Rolle | null | undefined,
  optionen: { nurLesend?: boolean; rechte?: readonly string[] } = {},
): boolean {
  if (optionen.nurLesend) return false;
  const rechte = optionen.rechte ?? [];
  return (
    rolle != null &&
    mindestens(rolle, "mitglied") &&
    (rechte.includes("gitlab.gate_uebersteuern") ||
      rechte.includes("system.admin"))
  );
}

export function darfMergeRequestMergen(
  rolle: Rolle | null | undefined,
  optionen: { nurLesend?: boolean; rechte?: readonly string[] } = {},
): boolean {
  if (optionen.nurLesend) return false;
  const rechte = optionen.rechte ?? [];
  return (
    rolle != null &&
    mindestens(rolle, "mitglied") &&
    (rechte.includes("gitlab.merge") || rechte.includes("system.admin"))
  );
}
